import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { formatApiError, fileUrl } from "@/lib/api";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Lock } from "lucide-react";

export default function AdminLogin() {
  useSeo({ title: "Admin Login | Maha Sampada", noindex: true });
  const { user, login } = useAuth();
  const config = useConfig();
  const logo = config.logo_url ? fileUrl(config.logo_url) : null;
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);

  useEffect(() => { if (user) navigate("/admin"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  const sendForgot = async () => {
    if (!forgotEmail.trim()) { toast.error("Enter your account email"); return; }
    setForgotSending(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      toast.success(data?.message || "If an account exists, a reset link has been sent.");
      setForgotOpen(false); setForgotEmail("");
    } catch (err) {
      // Neutral: still confirm regardless of any error to avoid revealing account existence
      toast.success("If an account exists for that email, a reset link has been sent.");
      setForgotOpen(false); setForgotEmail("");
    } finally { setForgotSending(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-charcoal px-4">
      <div className="w-full max-w-md rounded-3xl bg-white p-10">
        <div className="flex items-center gap-3">
          {logo ? (
            <img src={logo} alt="Maha Sampada" onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-14 w-auto max-w-[220px] object-contain" />
          ) : (
            <>
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-maroon text-cream font-display text-xl font-bold">M</div>
              <div><div className="font-display text-2xl font-bold text-charcoal">Maha Sampada</div><div className="overline text-[9px] text-saffron">Admin Dashboard</div></div>
            </>
          )}
        </div>
        <h1 className="mt-8 font-display text-2xl font-semibold text-charcoal">Sign in to continue</h1>
        <form onSubmit={submit} data-testid="login-form" className="mt-6 space-y-4">
          <div className="space-y-1.5"><Label className="font-accent text-xs font-semibold">Email</Label><Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
          <div className="space-y-1.5"><Label className="font-accent text-xs font-semibold">Password</Label><PasswordInput testId="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
          <div className="flex justify-end -mt-1">
            <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotEmail(email); setForgotOpen(true); }} className="font-accent text-xs text-saffron hover:underline">Forgot password?</button>
          </div>
          <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full rounded-full bg-saffron font-accent font-semibold text-white hover:bg-saffron-dark">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in…</> : <><Lock className="mr-2 h-4 w-4" />Sign In</>}
          </Button>
        </form>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent data-testid="forgot-password-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Reset your password</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Enter your account email. If it matches an active admin account, we'll send a single-use reset link that expires in 30 minutes.</p>
          <div className="space-y-1.5"><Label className="font-accent text-xs font-semibold">Account email</Label><Input data-testid="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@company.com" /></div>
          <DialogFooter>
            <Button data-testid="forgot-submit" disabled={forgotSending} onClick={sendForgot} className="rounded-full bg-saffron text-white hover:bg-saffron-dark">
              {forgotSending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</> : "Send reset link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
